import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { API_BASE_URL } from "./api";

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
  auth: boolean = false,
  isFormData: boolean = false
): Promise<Response> {
  const token = localStorage.getItem("token");
  const headers: Record<string,string> = {};
  
  if (data && !isFormData) headers["Content-Type"] = "application/json";
  if (auth && token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(url, {
    method,
    headers,
    body: data ? (isFormData ? data as FormData : JSON.stringify(data)) : undefined,
    credentials: "include",
  });

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    // Convert to string for cache config lookup
    const endpoint = typeof queryKey[0] === 'string' ? queryKey[0] : '';
    const token = localStorage.getItem("token");
    // Apply the specific cache configuration
    const cacheConfig = getCacheConfig(endpoint);
    
    // Make the API request
    const headers: Record<string, string> = {
      'Cache-Control': `max-age=${Math.floor(cacheConfig.staleTime / 1000)}`,
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const res = await fetch(queryKey[0] as string, {
      credentials: "include",
      // Add cache control headers based on stale time
      headers,
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

/**
 * Custom caching configuration for different API endpoints
 */
const CACHE_CONFIGS = {
  stores: {
    staleTime: 5 * 60 * 1000, // Stores data remains fresh for 5 minutes
    gcTime: 30 * 60 * 1000,   // Keep store data in cache for 30 minutes
  },
  orderPickups: {
    staleTime: 30 * 1000,     // Order pickup data remains fresh for 30 seconds
    gcTime: 10 * 60 * 1000,   // Keep order data in cache for 10 minutes
  },
  delivery: {
    staleTime: 10 * 1000,     // Delivery status refreshes more frequently
    gcTime: 5 * 60 * 1000,    // Keep delivery data in cache for 5 minutes
  },
  activeDelivery: {
    staleTime: 5 * 1000,      // Active deliveries need very frequent updates
    gcTime: 3 * 60 * 1000,    // But we still want to keep the data for a bit
  },
  user: {
    staleTime: 10 * 60 * 1000, // User data remains fresh for 10 minutes
    gcTime: 60 * 60 * 1000,    // Keep user data in cache for 1 hour
  },
  default: {
    staleTime: 60 * 1000,     // Default 1 minute stale time
    gcTime: 10 * 60 * 1000,   // Default 10 minute cache time
  }
};

/**
 * Get cache configuration based on endpoint path
 */
function getCacheConfig(endpoint: string) {
  if (endpoint.includes(`${API_BASE_URL}/api/stores`)) {
    return CACHE_CONFIGS.stores;
  }
  
  if (endpoint.includes(`${API_BASE_URL}/api/order-pickups`)) {
    return CACHE_CONFIGS.orderPickups;
  }
  
  if (endpoint.includes(`${API_BASE_URL}/api/users`)) {
    return CACHE_CONFIGS.user;
  }
  
  // For delivery tracking, check if it's for a specific delivery (likely active)
  if (endpoint.includes(`${API_BASE_URL}/api/doordash/deliveries/order/`)) {
    return CACHE_CONFIGS.activeDelivery;
  }
  
  // For other delivery endpoints (lists, general info)
  if (endpoint.includes(`${API_BASE_URL}/api/doordash/deliveries`)) {
    return CACHE_CONFIGS.delivery;
  }
  
  return CACHE_CONFIGS.default;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: true, // Enable refetch on window focus
      staleTime: 60 * 1000, // Default 1 minute stale time
      retry: 1, // Retry failed queries once
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
      gcTime: 10 * 60 * 1000, // Keep unused data in cache for 10 minutes
    },
    mutations: {
      retry: 1, // Retry failed mutations once
    },
  },
});


